import GokuDataGrid from '@/components/GokuDataGrid'
import Paper from '@/components/Paper'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CloseIcons from '@/icons/CloseIcons'
import EditIcons from '@/icons/EditIcons'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { GridColDefVNW } from 'vnetwork-ui/dist/components/DataGridPro'
import { getDefaultKPITargets, getEmployeeKPITargets } from '../../services'
import { KPITarget } from '../../types'
import CreateKPI from './CreateKPI'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface KPIManagementProps {
    employeeID?: number
}

const KPIManagement = ({ employeeID }: KPIManagementProps) => {
    const { t } = useTranslation()
    const [showForm, setShowForm] = useState(false)

    // Fetch employee specific KPI targets
    const { data: employeeKPIs, isLoading: loadingEmployeeKPIs } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.EMPLOYEE_KPI_TARGETS, employeeID],
            queryFn: () => getEmployeeKPITargets(employeeID || 0),
            enabled: !!employeeID,
        })

    // Fetch default KPI targets
    const { data: defaultKPIs, isLoading: loadingDefaultKPIs } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.DEFAULT_KPI_TARGETS],
            queryFn: getDefaultKPITargets,
        })

    // Merge employee KPIs with default KPIs for missing product groups
    const mergedKPIs = useMemo(() => {
        // If no default KPIs, return empty array
        if (!defaultKPIs?.results) {
            console.log('No default KPIs available')
            return []
        }

        // If employee has no KPIs, show all default KPIs
        if (!employeeKPIs?.results || employeeKPIs.results.length === 0) {
            return defaultKPIs.results.map(
                (defaultKPI, index) =>
                    ({
                        id: -(index + 1000), // Use negative IDs starting from -1000 for employee defaults
                        employee_id: employeeID,
                        is_default: true,
                        product_group_name: defaultKPI.product_group_name,
                        new_sale_rate: defaultKPI.default_new_sale_rate,
                        renewal_rate: defaultKPI.default_renewal_rate,
                        upsale_rate: defaultKPI.default_upsale_rate,
                        effective_date: defaultKPI.effective_date,
                    }) as KPITarget & { is_default: boolean }
            )
        }

        const employeeProductGroups = new Set(
            employeeKPIs.results.map((kpi) => kpi?.product_group_name)
        )
        console.log('employeeProductGroups:', employeeProductGroups)

        // Filter default KPIs that are not in the employee's product groups
        const missingDefaultKPIs = defaultKPIs.results
            .filter(
                (defaultKPI) =>
                    !employeeProductGroups?.has(defaultKPI?.product_group_name)
            )
            .map(
                (defaultKPI, index) =>
                    ({
                        id: -(index + 2000), // Use negative IDs starting from -2000 for missing defaults
                        employee_id: employeeID,
                        is_default: true,
                        product_group_name: defaultKPI.product_group_name,
                        new_sale_rate: defaultKPI.default_new_sale_rate,
                        renewal_rate: defaultKPI.default_renewal_rate,
                        upsale_rate: defaultKPI.default_upsale_rate,
                        effective_date: new Date().toISOString(),
                    }) as KPITarget & { is_default: boolean }
            )

        console.log('missingDefaultKPIs:', missingDefaultKPIs)

        const merged = [
            ...employeeKPIs.results.map((kpi) => ({
                ...kpi,
                is_default: false,
            })),
            ...missingDefaultKPIs,
        ]

        console.log('Final merged KPIs:', merged)
        return merged
    }, [employeeKPIs, defaultKPIs, employeeID])

    const handleAddNew = () => {
        setShowForm(true)
    }

    const handleFormClose = () => {
        setShowForm(false)
    }

    const columns: GridColDefVNW<KPITarget & { is_default: boolean }>[] = [
        {
            field: 'product_group_name',
            headerName: t('product_group_name'),
            width: 400,
            renderCell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <div
                        className={`w-2 h-2 rounded-full ${row.is_default ? 'bg-gray-400' : 'bg-blue-500'}`}
                    ></div>
                    <Typogaphy
                        className={
                            row.is_default ? 'text-gray-600' : 'text-gray-900'
                        }
                    >
                        {row.product_group_name}
                    </Typogaphy>
                    {row.is_default && (
                        <Typogaphy className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                            {t('kpis.target.default')}
                        </Typogaphy>
                    )}
                </div>
            ),
        },
        {
            field: 'new_sale_rate',
            headerName: t('new_sale_rate') + ' (%)',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <Typogaphy
                    className={
                        row.is_default ? 'text-gray-500' : 'text-success'
                    }
                >
                    {(row.new_sale_rate * 100).toFixed(2)} %
                </Typogaphy>
            ),
        },
        {
            field: 'renewal_rate',
            headerName: t('renewal_rate') + ' (%)',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <Typogaphy
                    className={
                        row.is_default ? 'text-gray-500' : 'text-primary'
                    }
                >
                    {(row.renewal_rate * 100).toFixed(2)} %
                </Typogaphy>
            ),
        },
        {
            field: 'upsale_rate',
            headerName: t('upsale_rate') + ' (%)',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <Typogaphy
                        className={
                            row.is_default ? 'text-gray-500' : 'text-primary'
                        }
                    >
                        {(row.upsale_rate * 100).toFixed(2)} %
                    </Typogaphy>
                </div>
            ),
        },
        {
            field: 'effective_date',
            headerName: t('effective_date'),
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <Typogaphy
                    className={
                        row.is_default ? 'text-gray-500' : 'text-gray-900'
                    }
                >
                    {dayjs(row.effective_date, 'YYYY-MM-DD').format(
                        'DD/MM/YYYY'
                    )}
                </Typogaphy>
            ),
        },
    ]

    // Show loading when either query is loading
    if (loadingDefaultKPIs || (employeeID && loadingEmployeeKPIs)) {
        return (
            <Paper>
                <div className="space-y-4 p-4">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </Paper>
        )
    }

    // If no employeeID, show default KPIs only
    if (!employeeID) {
        const defaultOnlyKPIs =
            defaultKPIs?.results?.map(
                (defaultKPI, index) =>
                    ({
                        id: -(index + 1), // Use negative IDs for default KPIs
                        employee_id: 0,
                        is_default: true,
                        product_group_name: defaultKPI.product_group_name,
                        new_sale_rate: defaultKPI.default_new_sale_rate,
                        renewal_rate: defaultKPI.default_renewal_rate,
                        upsale_rate: defaultKPI.default_upsale_rate,
                        effective_date: new Date().toISOString(),
                    }) as KPITarget & { is_default: boolean }
            ) || []

        return (
            <Paper>
                <div className="space-y-4">
                    <div className="text-center py-4">
                        <Typogaphy className="text-gray-600">
                            {t('kpis.no_employee_selected')}
                        </Typogaphy>
                    </div>

                    <GokuDataGrid
                        apiCall={async () => ({
                            results: defaultOnlyKPIs,
                            count: defaultOnlyKPIs.length,
                        })}
                        columns={columns}
                        options={{
                            showHideColumn: false,
                            title: undefined,
                        }}
                        dataGridProps={{
                            hideFooter: true,
                            paginationMode: 'client',
                            getRowId: (row) =>
                                row.id || `default-${row.product_group_name}`,
                            disableColumnMenu: true,
                            disableRowSelectionOnClick: true,
                            hideFooterPagination: false,
                        }}
                        reactQueryKey={[
                            'default-kpis',
                            JSON.stringify(defaultOnlyKPIs),
                        ]?.join('.')}
                        actions={[]}
                    />
                </div>
            </Paper>
        )
    }

    return (
        <Paper>
            <div className="space-y-4">
                <div className="flex items-center justify-end">
                    <Button
                        variant="outlined"
                        startIcon={showForm ? <CloseIcons /> : <EditIcons />}
                        onClick={() =>
                            showForm ? handleFormClose() : handleAddNew()
                        }
                        size="small"
                    >
                        {showForm ? t('cancel') : t('kpis.targets.upsert')}
                    </Button>
                </div>

                {showForm && (
                    <CreateKPI
                        employeeId={employeeID}
                        existingKPIs={employeeKPIs?.results?.map((kpi) => ({
                            ...kpi,
                            new_sale_rate: kpi.new_sale_rate * 100,
                            renewal_rate: kpi.renewal_rate * 100,
                            upsale_rate: kpi.upsale_rate * 100,
                        }))}
                    />
                )}

                <GokuDataGrid
                    apiCall={async () => {
                        const result = {
                            results: mergedKPIs,
                            count: mergedKPIs.length,
                        }
                        return result
                    }}
                    columns={columns}
                    options={{
                        showHideColumn: false,
                        title: undefined,
                    }}
                    dataGridProps={{
                        hideFooter: true,
                        paginationMode: 'client',
                        getRowId: (row) =>
                            row.id || `default-${row.product_group_name}`,
                        disableColumnMenu: true,
                        disableRowSelectionOnClick: true,
                        hideFooterPagination: false,
                    }}
                    reactQueryKey={[
                        'kpis',
                        employeeID,
                        JSON.stringify(mergedKPIs),
                    ]?.join('.')}
                    actions={[]}
                />
            </div>
        </Paper>
    )
}

export default KPIManagement
